import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonLogging = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Logging for Production</h1>
                <p className="subtitle">If you didn't log it, it didn't happen. Building the Flight Recorder for your application.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine an Airplane (Your App).
                    </p>
                    <ul>
                        <li><strong>Print Statements:</strong> This is the pilot shouting "We are flying!" out the window. If the plane crashes, nobody hears the shout. The information is lost forever.</li>
                        <li><strong>Logging:</strong> This is the <strong>Black Box Flight Recorder</strong>. It silently writes everything ("Engine started", "Altitude 10k", "Engine failure") to a fireproof box (Log File). If the plane crashes, investigators open the box to find out <em>exactly</em> what happened.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Levels of Severity</h2>
                <p>
                    Not all logs are equal. Python's `logging` module has 5 standard levels.
                </p>
                <ul className="premium-list">
                    <li><strong>DEBUG (10):</strong> "Variable x is 5". Noisy details for developers only.</li>
                    <li><strong>INFO (20):</strong> "User logged in". Normal system events.</li>
                    <li><strong>WARNING (30):</strong> "Disk 90% full". Potential problem, but app is fine.</li>
                    <li><strong>ERROR (40):</strong> "Database connection failed". A specific request failed.</li>
                    <li><strong>CRITICAL (50):</strong> "System out of memory". The app is crashing.</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>3. Basic Configuration</h2>
                <p>
                    By default, Python logs strictly to the console. Stick this at the top of your script.
                </p>
                <CodeBlock language="python" code={`import logging

# Configure the system ONCE at startup
logging.basicConfig(
    level=logging.INFO, # Ignore DEBUG logs
    format='%(asctime)s - %(levelname)s - %(message)s',
    filename='app.log', # Write to file instead of console
    filemode='a' # Append (Don't overwrite)
)

logging.info("System is starting...")
logging.warning("Config file missing, using defaults.")
logging.error("Unable to contact Payment Gateway!")`} />
            </div>

            <div className="content-section">
                <h2>4. Deep Dive: Structured Logging (JSON)</h2>
                <p>
                    Text logs (`"User logged in"`) are hard for computers to search.
                    <strong>Structured Logging</strong> saves logs as JSON objects. This allows tools like <strong>Datadog</strong> or <strong>Splunk</strong> to graph your data.
                </p>
                <CodeBlock language="python" code={`# The Old Way (String)
logging.info(f"Order {order_id} failed for user {user_id}")

# The Modern Way (Structured)
# Using a library like 'structlog' or 'python-json-logger'
logger.info("order_failed", extra={
    "order_id": 999, 
    "user_id": 55, 
    "reason": "Card Declined"
})

# Result in file:
# {"timestamp": "2023-10-01...", "event": "order_failed", "order_id": 999, ...}`} />
            </div>

            <div className="content-section">
                <h2>5. Advanced: Rotating File Handlers</h2>
                <p>
                    If you log everything, your `app.log` will grow to 500GB and crash the server.
                    Use `RotatingFileHandler` to keep only the last 5 files of 10MB each.
                </p>
                <CodeBlock language="python" code={`from logging.handlers import RotatingFileHandler

handler = RotatingFileHandler(
    'app.log', 
    maxBytes=10_000_000, # 10 MB usage limit
    backupCount=5        # Keep 5 backups
)

logger = logging.getLogger('my_app')
logger.addHandler(handler)`} />
            </div>

        </div>
    );
};

export default PythonLogging;

